/**
 * 사용자 관련 컴포넌트
 * - 사용자 프로필 UI 업데이트
 * - 사용자 정보 관리
 */
import { updateUserProfile } from '../store/actions/userActions.js';
import { store } from '../store/index.js';
import { updateElementContent, updateElementVisibility, showToast } from '../utils/ui-manager.js';
import { set, ref, get } from 'firebase/database';
import { database, storage } from '../utils/firebase-config.js';
import { uploadBytes, getDownloadURL, ref as storageRef } from 'firebase/storage';

/**
 * 사용자 UI 업데이트 함수
 * @param {Object|null} user - 사용자 데이터 객체 또는 null
 */
export const updateUserUI = (user) => {
  // 유저 프로필 UI 업데이트
  if (user) {
    // 로그인된 상태
    updateElementVisibility('#user-info', true);
    updateElementVisibility('#post-create-card', true);
    updateElementVisibility('#login-card', false);
    updateElementVisibility('#posts-container', true);
    updateElementVisibility('#sidebar-profile', true);
    updateElementVisibility('#sidebar-login', false);
    
    // 프로필 이미지 및 이름 업데이트
    document.getElementById('user-avatar').src = user.photoURL;
    
    // 사이드바 프로필 정보 업데이트
    updateElementContent('#sidebar-name', user.displayName);
    updateElementContent('#sidebar-email', user.email);
    document.getElementById('sidebar-avatar').src = user.photoURL;
    
    // 게시물 작성 카드 프로필 이미지 업데이트
    document.getElementById('post-user-avatar').src = user.photoURL;
    
    // 통합된 인증 버튼 업데이트
    const authBtn = document.getElementById('auth-btn');
    authBtn.innerHTML = '<i class="bi bi-box-arrow-right fs-5"></i>';
    authBtn.title = '로그아웃';
    
    // 사용자 정보를 Firebase에서 가져오기
    loadUserProfile(user.uid);
  } else {
    // 로그아웃된 상태
    updateElementVisibility('#user-info', false);
    updateElementVisibility('#post-create-card', false);
    updateElementVisibility('#login-card', true);
    updateElementVisibility('#posts-container', false);
    updateElementVisibility('#sidebar-profile', false);
    updateElementVisibility('#sidebar-login', true);
    
    // 통합된 인증 버튼 업데이트
    const authBtn = document.getElementById('auth-btn');
    authBtn.innerHTML = '<i class="bi bi-person fs-5"></i>';
    authBtn.title = '로그인';
  }
};

/**
 * 사용자 프로필 정보 불러오기
 * @param {string} userId - 사용자 ID
 */
export const loadUserProfile = async (userId) => {
  try {
    const userProfileRef = ref(database, `users/${userId}/profile`);
    const snapshot = await get(userProfileRef);
    
    if (snapshot.exists()) {
      const profileData = snapshot.val();
      store.dispatch(updateUserProfile(profileData));
    } else {
      // 프로필 정보가 없으면 기본 프로필 생성
      const defaultProfile = {
        bio: '',
        location: '',
        website: '',
        joinDate: new Date().toISOString()
      };
      
      await set(userProfileRef, defaultProfile);
      store.dispatch(updateUserProfile(defaultProfile));
    }
  } catch (error) {
    console.error('프로필 정보 로드 중 오류 발생:', error);
    showToast('프로필 정보를 불러오는 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 사용자 프로필 업데이트
 * @param {string} userId - 사용자 ID
 * @param {Object} profileData - 업데이트할 프로필 데이터
 */
export const updateProfile = async (userId, profileData) => {
  try {
    const userProfileRef = ref(database, `users/${userId}/profile`);
    await set(userProfileRef, {
      ...profileData,
      updatedAt: new Date().toISOString()
    });
    
    store.dispatch(updateUserProfile(profileData));
    showToast('프로필이 업데이트되었습니다.', 'success');
  } catch (error) {
    console.error('프로필 업데이트 중 오류 발생:', error);
    showToast('프로필 업데이트 중 오류가 발생했습니다.', 'error');
  }
};

/**
 * 프로필 이미지 업로드
 * @param {string} userId - 사용자 ID
 * @param {File} imageFile - 업로드할 이미지 파일
 */
export const uploadProfileImage = async (userId, imageFile) => {
  try {
    const imageRef = storageRef(storage, `users/${userId}/profile/${Date.now()}-${imageFile.name}`);
    await uploadBytes(imageRef, imageFile);
    
    const downloadURL = await getDownloadURL(imageRef);
    
    // 프로필 이미지 URL 업데이트
    const userRef = ref(database, `users/${userId}`);
    await set(userRef, {
      photoURL: downloadURL,
      updatedAt: new Date().toISOString()
    });
    
    // 스토어 업데이트
    store.dispatch(updateUserProfile({ photoURL: downloadURL }));
    
    // UI 업데이트
    document.getElementById('user-avatar').src = downloadURL;
    document.getElementById('sidebar-avatar').src = downloadURL;
    document.getElementById('post-user-avatar').src = downloadURL;
    
    showToast('프로필 이미지가 업데이트되었습니다.', 'success');
  } catch (error) {
    console.error('프로필 이미지 업로드 중 오류 발생:', error);
    showToast('프로필 이미지 업로드 중 오류가 발생했습니다.', 'error');
  }
}; 